import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { api } from '../../lib/api.js'
import useSaveShortcut from '../../lib/useSaveShortcut.js'
import { toast } from '../../components/feedback/Toast.jsx'

/**
 * Create / edit a login. Full-screen sheet on phones, centred card on
 * desktop. On edit, the password field starts empty and means "unchanged" —
 * the current value is never round-tripped into a form.
 */
export default function LoginForm({ initial, onClose }) {
  const qc = useQueryClient()
  const editing = Boolean(initial?.id)
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const isAdmin = me.data?.user?.role === 'admin'
  const embargo = initial?.collection === 'embargo'
  const [form, setForm] = useState({
    label: initial?.label || '',
    username: initial?.username || '',
    secret: '',
    url: initial?.url || '',
    notes: initial?.notes || '',
    restricted: Boolean(initial?.restricted),
  })
  const [busy, setBusy] = useState(false)
  const formRef = useRef(null)

  useSaveShortcut(() => formRef.current?.requestSubmit())

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const body = {
        label: form.label.trim(),
        username: form.username.trim() || null,
        url: form.url.trim() || null,
        notes: form.notes.trim() || null,
      }
      if (isAdmin && !embargo) body.restricted = form.restricted
      // A new login joins the page it was made on; an edit stays put.
      if (!editing && embargo) body.collection = 'embargo'
      // Only send a secret that was actually typed; null means "keep".
      if (form.secret) body.secret = form.secret
      if (editing) {
        await api(`/credentials/${initial.id}`, { method: 'PATCH', body })
      } else {
        await api('/credentials', { body })
      }
      qc.invalidateQueries({ queryKey: ['credentials'] })
      qc.invalidateQueries({ queryKey: ['search'] })
      toast(editing ? 'Login updated' : 'Login added')
      onClose()
    } catch (err) {
      toast(err.message || 'Couldn’t save', 'error')
    } finally {
      setBusy(false)
    }
  }

  const field =
    'tap w-full rounded-xl border border-hairline bg-grouped px-4 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link'

  // Portaled to <body>: rendered inline it sits inside the shell's scroll
  // pane, where iOS treats fixed as absolute — the sheet then ends at the
  // pane's bottom edge and the tab bar covers the Save button.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 md:items-center" onClick={onClose}>
      <div
        className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom))] md:max-w-md md:rounded-2xl md:pb-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">{editing ? 'Edit login' : 'New login'}</h2>
          <button onClick={onClose} className="tap flex items-center justify-center rounded-lg text-muted active:bg-grouped">
            <X size={20} />
          </button>
        </div>

        <form ref={formRef} onSubmit={submit} className="space-y-3">
          <input required placeholder="Name (e.g. Zoom — office@)" value={form.label} onChange={set('label')} className={field} />
          <input placeholder="Username / email" value={form.username} onChange={set('username')} autoComplete="off" className={field} />
          <input
            placeholder={editing ? 'Password (leave blank to keep current)' : 'Password'}
            value={form.secret}
            onChange={set('secret')}
            autoComplete="off"
            className={`${field} font-mono`}
          />
          <input placeholder="Website (optional)" value={form.url} onChange={set('url')} inputMode="url" className={field} />
          <textarea placeholder="Notes (optional)" value={form.notes} onChange={set('notes')} rows={2} className={field} />

          {/* Admin-only would move an embargo login onto the Managerial page,
              so it isn't offered there. */}
          {isAdmin && !embargo && (
            <label className="flex items-center gap-2.5 px-1 py-1 text-sm text-ink">
              <input
                type="checkbox"
                checked={form.restricted}
                onChange={(e) => setForm((f) => ({ ...f, restricted: e.target.checked }))}
                className="h-4 w-4 accent-[--color-navy]"
              />
              Admin only — hidden from editors and viewers (CDM Management)
            </label>
          )}

          <button type="submit" disabled={busy || !form.label.trim()} className="tap w-full rounded-xl bg-navy py-3 font-medium text-white disabled:opacity-50">
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add login'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  )
}
